import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  MenuItem,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { Grid } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { playerApi } from '../services/api';
import { PaginatedResponse, Player } from '../models';


interface FilterState {
  role: string;
  is_international: string;
  gender: string;
  search: string;
  ordering: string;
}

const validationSchema = yup.object({
  name: yup.string().required('Name is required'),
  role: yup.string().nullable(),
  batting: yup.string().nullable(),
  bowling: yup.string().nullable(),
  country: yup.number().nullable(),
});

const Players: React.FC = () => {
  const [players, setPlayers] = useState<Player[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState<Player | null>(null);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [filters, setFilters] = useState<FilterState>({
    role: '',
    is_international: '',
    gender: '',
    search: '',
    ordering: '',
  });

  const roleOptions = [
    { value: '', label: 'All' },
    { value: 'BAT', label: 'Batsman' },
    { value: 'BOWL', label: 'Bowler' },
    { value: 'ALL', label: 'All-Rounder' },
    { value: 'WK', label: 'Wicket-Keeper' },
  ];

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 70, sortable: true, filterable: true, type: 'number' },
    { 
      field: 'image',
      headerName: 'Image',
      width: 80,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        params.value ? 
        <img 
          src={params.value} 
          alt={params.row.name} 
          style={{ width: '40px', height: '40px', borderRadius: '50%' }}
        /> : 
        <Box 
          sx={{ 
            width: '40px', 
            height: '40px', 
            borderRadius: '50%', 
            bgcolor: 'grey.300',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          {params.row.name.charAt(0).toUpperCase()}
        </Box>
      )
    },
    { field: 'name', headerName: 'Name', width: 200, sortable: true, filterable: true },
    { field: 'd_name', headerName: 'Dream11 Name', width: 150, sortable: true, filterable: true },
    { 
      field: 'role', 
      headerName: 'Role', 
      width: 120, 
      sortable: true,
      filterable: true,
      type: 'singleSelect',
      valueOptions: roleOptions.map(opt => opt.value)
    },
    { field: 'batting', headerName: 'Batting', width: 150, sortable: true, filterable: true },
    { field: 'bowling', headerName: 'Bowling', width: 150, sortable: true, filterable: true },
    { 
      field: 'd_verified', 
      headerName: 'Verified', 
      width: 100, 
      type: 'boolean',
      sortable: true,
      filterable: true,
      renderCell: (params) => (
        <Box sx={{ color: params.value ? 'success.main' : 'error.main' }}>
          {params.value ? 'Yes' : 'No'}
        </Box>
      )
    },
    { 
      field: 'is_international', 
      headerName: 'International', 
      width: 120, 
      type: 'boolean',
      sortable: true,
      filterable: true,
      renderCell: (params) => (
        <Box sx={{ color: params.value ? 'success.main' : 'error.main' }}>
          {params.value ? 'Yes' : 'No'}
        </Box>
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 300,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box>
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={() => handleEdit(params.row)}
            sx={{ mr: 1 }}
          >
            Edit
          </Button>
          <Button
            variant="contained"
            color="secondary"
            size="small"
            onClick={() => handleRefreshStats(params.row.id)}
            sx={{ mr: 1 }}
          >
            Refresh Stats
          </Button>
          <Button
            variant="contained"
            color="error"
            size="small"
            onClick={() => handleDelete(params.row.id)}
          >
            Delete
          </Button>
        </Box>
      ),
    },
  ];

  const formik = useFormik<Player>({
    initialValues: {
      id: 0,
      name: '',
      d_name: null,
      c_name: null,
      mt_name: null,
      d_id: null,
      e_id: null,
      c_id: null,
      e_o_id: null,
      country: null,
      role: null,
      batting: null,
      bowling: null,
      d_verified: false,
      is_international: false,
      gender: null,
      notes: null,
      image: null,
      image_d: null,
      image_headshot: null
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        if (selectedPlayer) {
          await playerApi.update(selectedPlayer.id, values);
        } else {
          await playerApi.create(values);
        }
        fetchPlayers(page);
        handleClose();
      } catch (error) {
        console.error('Error saving player:', error);
      }
    },
  });

  const fetchPlayers = async (pageNumber: number = 1, size: number = pageSize) => {
    try {
      const params: any = {
        page: pageNumber,
        page_size: size,
      };

      if (filters.role) {
        params.role = filters.role;
      }
      
      if (filters.is_international) {
        console.log('is_international', filters.is_international);
        params.is_international = filters.is_international === 'true';
      }

      if (filters.gender) {
        params.gender = filters.gender;
      }

      if (filters.search) {
        params.search = filters.search;
      }

      if (filters.ordering) {
        params.ordering = filters.ordering;
      }

      const response = await playerApi.getAll(params);
      const data = response.data as PaginatedResponse<Player>;
      setPlayers(data.results);
      setTotalCount(data.count);
    } catch (error) {
      console.error('Error fetching players:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSortModelChange = (model: any) => {
    if (model.length > 0) {
      const { field, sort } = model[0];
      const ordering = sort === 'desc' ? `-${field}` : field;
      setFilters(prev => ({
        ...prev,
        ordering
      }));
    } else {
      setFilters(prev => ({
        ...prev,
        ordering: ''
      }));
    }
  };

  const handleFilterModelChange = (model: any) => {
    const newFilters = { ...filters };
    
    if (model.items) {
      model.items.forEach((item: any) => {
        if (item.field === 'role') {
          newFilters.role = item.value;
        } else if (item.field === 'is_international') {
          newFilters.is_international = item.value;
        } else if (item.field === 'gender') {
          newFilters.gender = item.value;
        } else if (item.field === 'name' || item.field === 'd_name') {
          newFilters.search = item.value;
        }
      });
    }

    setFilters(newFilters);
  };

  useEffect(() => {
    fetchPlayers(page, pageSize);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, filters]);

  const handleEdit = (player: Player) => {
    setSelectedPlayer(player);
    formik.setValues(player);
    setOpen(true);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this player?')) {
      try {
        await playerApi.delete(id);
        fetchPlayers(page);
      } catch (error) {
        console.error('Error deleting player:', error);
      }
    }
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedPlayer(null);
    formik.resetForm();
  };

  const handleRefreshStats = async (playerId: number) => {
    try {
      await playerApi.refreshStats(playerId);
      alert('Stats refresh triggered successfully');
    } catch (error) {
      console.error('Error refreshing player stats:', error);
      alert('Failed to refresh player stats');
    }
  };

  return (
    <Box sx={{ height: 'calc(100vh - 320px)', width: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h4">Players</Typography>
        <Button variant="contained" color="primary" onClick={() => setOpen(true)}>
          Add Player
        </Button>
      </Box>

      <DataGrid
        rows={players}
        columns={columns}
        initialState={{
          pagination: {
            paginationModel: { pageSize: 50, page: 0 },
          },
          columns: {
            columnVisibilityModel: {
              d_id: false,
              e_id: false,
              c_id: false,
              e_o_id: false
            },
          },
        }}
        pageSizeOptions={[10, 25, 50, 100]}
        checkboxSelection
        disableRowSelectionOnClick
        loading={loading}
        rowCount={totalCount}
        paginationMode="server"
        sortingMode="server"
        filterMode="server"
        onSortModelChange={handleSortModelChange}
        onFilterModelChange={handleFilterModelChange}
        onPaginationModelChange={(model) => {
          setPage(model.page + 1);
          setPageSize(model.pageSize);
        }}
      />

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedPlayer ? `Edit Player: ${selectedPlayer.name}` : 'Add Player'}
        </DialogTitle>
        <form onSubmit={formik.handleSubmit}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  margin="normal"
                  name="name"
                  label="Name"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  error={formik.touched.name && Boolean(formik.errors.name)}
                  helperText={formik.touched.name && formik.errors.name}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  margin="normal"
                  name="d_name"
                  label="Dream11 Name"
                  value={formik.values.d_name || ''}
                  onChange={formik.handleChange}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  margin="normal"
                  name="c_name"
                  label="Circle Name"
                  value={formik.values.c_name || ''}
                  onChange={formik.handleChange}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  margin="normal"
                  name="mt_name"
                  label="MyTeam11 Name"
                  value={formik.values.mt_name || ''}
                  onChange={formik.handleChange}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  margin="normal"
                  name="role"
                  label="Role"
                  select
                  value={formik.values.role || ''}
                  onChange={formik.handleChange}
                >
                  <MenuItem value="">--Select Role--</MenuItem>
                  <MenuItem value="BAT">Batsman</MenuItem>
                  <MenuItem value="BOWL">Bowler</MenuItem>
                  <MenuItem value="ALL">All-Rounder</MenuItem>
                  <MenuItem value="WK">Wicket-Keeper</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  margin="normal"
                  name="country"
                  label="Country ID"
                  type="number"
                  value={formik.values.country || ''}
                  onChange={formik.handleChange}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  margin="normal"
                  name="batting"
                  label="Batting Style"
                  value={formik.values.batting || ''}
                  onChange={formik.handleChange}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  margin="normal"
                  name="bowling"
                  label="Bowling Style"
                  value={formik.values.bowling || ''}
                  onChange={formik.handleChange}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  margin="normal"
                  name="d_id"
                  label="Dream11 ID"
                  type="number"
                  value={formik.values.d_id || ''}
                  onChange={formik.handleChange}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  margin="normal"
                  name="e_id"
                  label="ESPN ID"
                  type="number"
                  value={formik.values.e_id || ''}
                  onChange={formik.handleChange}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  margin="normal"
                  name="c_id"
                  label="Circle ID"
                  type="number"
                  value={formik.values.c_id || ''}
                  onChange={formik.handleChange}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  margin="normal"
                  name="gender"
                  label="Gender"
                  select
                  value={formik.values.gender || ''}
                  onChange={formik.handleChange}
                >
                  <MenuItem value="">--Select Gender--</MenuItem>
                  <MenuItem value="M">Male</MenuItem>
                  <MenuItem value="F">Female</MenuItem>
                  <MenuItem value="O">Other</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  margin="normal"
                  name="slug"
                  label="Slug"
                  value={formik.values.slug || ''}
                  onChange={formik.handleChange}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  margin="normal"
                  name="notes"
                  label="Notes"
                  multiline
                  rows={3}
                  value={formik.values.notes || ''}
                  onChange={formik.handleChange}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formik.values.d_verified || false}
                      onChange={formik.handleChange}
                      name="d_verified"
                    />
                  }
                  label="Dream11 Verified"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formik.values.is_international || false}
                      onChange={formik.handleChange}
                      name="is_international"
                    />
                  }
                  label="International Player"
                />
              </Grid>
              {selectedPlayer && selectedPlayer.image && (
                <Grid item xs={12}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle1">Current Image</Typography>
                    <img 
                      src={selectedPlayer.image}
                      alt={selectedPlayer.name}
                      style={{ maxWidth: '100px', maxHeight: '100px' }}
                    />
                  </Box>
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Button type="submit" variant="contained" color="primary">
              {selectedPlayer ? 'Update' : 'Add'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default Players;